import { UnitData } from "../lib/db/types.js";
import { groupBy } from "../lib/group-by.js";
import { sortByName } from "../lib/sort.js";
import { Ability } from "./ability.js";
import { Army } from "./army.js";
import { checkCondition, isCondition } from "./condition.js";
import { Keyword } from "./keyword.js";
import { Ruleset } from "./ruleset.js";
import { applyModifiers } from "./stat-modifier.js";

export type UnitProfile = {
  id: string;
  name: string;
  cost: number;
  movement: number;
  combat: number;
  defense: number;
  morale: number;
  range: number;
  base_min: number;
  base_max: number;
};

export class Unit {
  readonly ruleset: Ruleset;
  readonly army: Army;
  readonly data: UnitData;
  readonly profile: UnitProfile;

  constructor(army: Army, unitProfile: UnitProfile, unitData: UnitData) {
    this.ruleset = army.ruleset;
    this.army = army;
    this.data = unitData;
    this.profile = unitProfile;
  }

  get name() {
    return this.data.name || (this.isLeader ? "Leader" : this.profile.name);
  }

  get cost() {
    const armyKeyword = this.army.keyword;
    const keywordsCost = this.keywords.reduce((acc, keyword) => {
      const keywordCost = keyword.cost ?? 0;

      // Make sure that keyword is not purchased more times than is allowed
      const clampedPurchaseCount = Math.min(
        keyword.definition.max_purchase_count,
        keyword.ref.count,
      );

      // If the keyword is an army keyword, substract the army count (which should be
      // always 1) from the clamped purchase count to make sure the appropriate
      // discount is applied.
      const purchaseCount =
        armyKeyword?.id === keyword.id
          ? clampedPurchaseCount - armyKeyword.ref.count
          : clampedPurchaseCount;

      return acc + keywordCost * purchaseCount;
    }, 0);
    const abilitiesCost = this.abilities.reduce((acc, a) => acc + a.cost, 0);
    const totalUnitCost = this.profile.cost + keywordsCost + abilitiesCost;

    // Make sure that the minimum unit cost is respected
    return Math.max(this.ruleset.minUnitCost, totalUnitCost);
  }

  get baseSize() {
    const thinSpace = "\u2009";
    return `${this.profile.base_min}mm${thinSpace}–${thinSpace}${this.profile.base_max}mm Base`;
  }

  get summary() {
    const base = `${this.cost}pts, ${this.profile.name}`;

    const names = [
      ...this.keywords.map((k) => `${k.name}${k.ref.count > 1 ? " x2" : ""}`),
      ...this.abilities.map((a) => a.name),
    ];

    return names.length > 0 ? `${base} · ${names.join(", ")}` : base;
  }

  /**
   * Keywords that were directly assigned to this unit.
   */
  get unitKeywords() {
    return this.data.keywords
      .map((keywordRef) => Keyword.from(this, keywordRef))
      .filter((k) => !!k)
      .sort(sortByName);
  }

  /**
   * Resolves the army keyword so that if a unit's profile doesn't match the
   * constraint, it is not applied.
   *
   * Note that we are intentionally not checking all conditions, as that
   * creates potential for keyword resolution loops, and is kinda jank.
   */
  get armyKeyword() {
    const armyKeyword = this.army.keyword;

    if (!armyKeyword) {
      return null;
    }

    const { conditions = [] } = armyKeyword.definition;
    const allPass = conditions.filter(isCondition).every((condition) => {
      switch (condition.type) {
        case "HAS_PROFILE": {
          return this.profile.id === condition.value;
        }

        case "HAS_NOT_PROFILE": {
          return this.profile.id !== condition.value;
        }

        default: {
          return true;
        }
      }
    });

    // If some of the conditions don't pass, we don't want to apply this army
    // keyword to this unit.
    if (!allPass) {
      return null;
    }

    return armyKeyword;
  }

  /**
   * Resolves an army keyword that was removed due to incompatible unit type.
   * If no army keyword was selected, or it was applied, `null` is returned.
   */
  get removedArmyKeyword() {
    if (this.army.keyword && !this.armyKeyword) {
      return this.army.keyword;
    }

    return null;
  }

  get keywords() {
    const armyKeyword = this.armyKeyword;

    // If an army keyword exists, we must take into account that it can exist both
    // as an army keyword and a unit keyword. This can happen because of purchasing
    // the keyword at x2 for the unit, or because it was initially purchased for
    // the unit and only after it was purchased for the army.
    if (armyKeyword) {
      const duplicateKeywordRef = this.unitKeywords.find(
        (k) => k.id === armyKeyword.id,
      )?.ref;

      // If a duplicate keyword is found, we clone the existing army keyword with a new
      // synthetic ref that selects the larger count between the two refs
      const mergedKeyword = duplicateKeywordRef
        ? Keyword.from(this, {
            ...armyKeyword.ref,

            // We take whichever count is higher. I.e. if the unit keyword was taken twice,
            // we respect that, otherwise it is taken once only.
            count: Math.max(duplicateKeywordRef.count, armyKeyword.ref.count),
          })
        : armyKeyword;

      if (mergedKeyword) {
        return [
          mergedKeyword,
          ...this.unitKeywords.filter((k) => k.id !== mergedKeyword.id),
        ];
      }

      return this.unitKeywords;
    }

    return this.unitKeywords;
  }

  get abilities() {
    return this.data.abilities
      .map((abilityRef) => Ability.from(this, abilityRef))
      .filter((a) => !!a)
      .sort(sortByName);
  }

  get modifiers() {
    return groupBy(
      this.keywords.flatMap((keyword) => {
        // Modifiers must be returned as many times as the keyword was
        // purchased. However, this number must not exceed the maximum allowed
        // purchase count.
        const clampedCount = Math.min(
          keyword.definition.max_purchase_count,
          keyword.ref.count,
        );

        return Array.from({ length: clampedCount }).flatMap(
          () => keyword.modifiers ?? [],
        );
      }),
      (modifier) => modifier.stat,
    );
  }

  get movement() {
    return applyModifiers(this, this.modifiers.movement, this.profile.movement);
  }

  /**
   * The unit's combat value before ranged/melee specific modifiers are taken
   * into account. Use {@link rangedCombat} or {@link meleeCombat} to get the
   * value a unit actually rolls against.
   */
  get combat() {
    return applyModifiers(this, this.modifiers.combat, this.profile.combat);
  }

  get rangedCombat() {
    return applyModifiers(this, this.modifiers.ranged_combat, this.combat);
  }

  get meleeCombat() {
    return applyModifiers(this, this.modifiers.melee_combat, this.combat);
  }

  get defense() {
    return applyModifiers(this, this.modifiers.defense, this.profile.defense);
  }

  get morale() {
    return applyModifiers(this, this.modifiers.morale, this.profile.morale);
  }

  get range() {
    return applyModifiers(this, this.modifiers.range, this.profile.range);
  }

  get failingConditions() {
    return this.keywords.flatMap((keyword) => {
      return (
        keyword.definition.conditions
          ?.filter(isCondition)
          .filter((condition) => !checkCondition(keyword.id, this)(condition))
          .map((condition) => {
            switch (condition.type) {
              case "HAS_KEYWORD":
              case "HAS_NOT_KEYWORD": {
                const conditionKeywordDef = this.ruleset.keywordDefs.get(
                  condition.value,
                );
                if (!conditionKeywordDef) {
                  return null;
                }

                return {
                  type: condition.type,
                  keyword,
                  conditionKeywordDef,
                };
              }

              case "HAS_PROFILE":
              case "HAS_NOT_PROFILE": {
                const profile = this.ruleset.unitProfiles.get(condition.value);
                if (!profile) {
                  return null;
                }

                return {
                  type: condition.type,
                  keyword,
                  profile,
                };
              }

              case "MAX_COUNT_IN_ARMY": {
                return {
                  type: condition.type,
                  keyword,
                  maxCount: condition.value,
                };
              }

              default: {
                return null;
              }
            }
          })
          .filter((c) => !!c) ?? []
      );
    });
  }

  get isValid() {
    return this.failingConditions.length === 0;
  }

  get isLeader() {
    return this.keywords.some((k) => k.id === "LEADER");
  }

  static from(army: Army, unitData: UnitData) {
    const unitProfile = army.ruleset.unitProfiles.get(unitData.type);

    if (!unitProfile) {
      return null;
    }

    return new Unit(army, unitProfile, unitData);
  }
}
